/**
 * @file NotificationTemplateTabsPage.js
 * @description Create/edit/view page for a multitenant notification template. This is a system-scoped,
 * agency/global-scoped feature and intentionally excludes client-message-only concepts such as audience,
 * schedules, and tenantId.
 **/
import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './NotificationTemplateTabsPage.module.scss';
import Toast from '../../ui/toasts/Toast';
import Spinner from '../../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import NotificationTemplateDetailsPanel from './NotificationTemplateDetailsPanel';
import NotificationTemplateContentPreview from './NotificationTemplateContentPreview';
import {
    getNotificationTemplateById,
    createNotificationTemplate,
    updateNotificationTemplate,
    getNotificationCategories,
    getAgencies,
    mapTemplateForEdit,
} from '../../../services/notificationTemplateService';
import { getClientNotificationTemplateById } from '../../../services/clientNotificationTemplateService';
import { useAuthContext } from '../../../store/Contexts';
import { ROUTES } from '../../../constants/routes';
import { getText } from '../../../i18n';
import {
    PushDestinationType,
    SecurityAttributes,
} from '../../../utils/data/enums';
import NotificationPageFrame from '../NotificationPageFrame';

const EMPTY_TEMPLATE = {
    agencyId: 0,
    availableTierIds: [],
    notificationStatusId: 1,
    pushDestinationTypeId: PushDestinationType.FullMessage,
};

// Notification templates are one-off content, not recurring sends; backend requires this on save.
const NOTIFICATION_FREQUENCY_AD_HOC = 2;

const hasMeaningfulHtml = html => {
    if (!html) return false;
    return (
        html
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim().length > 0
    );
};

const buildValidationResult = (isValid, message = null) => ({
    isValid,
    message,
});

/**
 * Create/edit/view page for a multitenant notification template.
 * @returns {JSX.Element} NotificationTemplateTabsPage component.
 */
const NotificationTemplateTabsPage = ({
    isCreating = false,
    isClientView = false,
}) => {
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { notificationId } = useParams();
    const { user, hasSecurityAttribute } = useAuthContext();
    const isReadOnly =
        isClientView ||
        !hasSecurityAttribute([
            SecurityAttributes.MultiTenantNotificationTemplatesFullAccess,
        ]);

    const [templateData, setTemplateData] = useState(EMPTY_TEMPLATE);
    const [originalTemplateData, setOriginalTemplateData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [agencyOptions, setAgencyOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const loadTemplate = useCallback(async () => {
        setIsLoading(true);
        try {
            const [rawTemplate, fetchedCategories, fetchedAgencies] =
                await Promise.all([
                    isCreating
                        ? Promise.resolve(EMPTY_TEMPLATE)
                        : isClientView
                          ? getClientNotificationTemplateById(
                                null,
                                notificationId,
                            )
                          : getNotificationTemplateById(notificationId),
                    getNotificationCategories(),
                    getAgencies(),
                ]);

            // Users may only view/select agencies they actually have access to; Global (0) is only
            // offered when the user's JWT explicitly grants agency 0. Some agency records are
            // themselves named "Global", so exclude those to avoid a duplicate Global entry.
            const allowedAgencyIds = user?.jwt?.agencies || [];
            const hasGlobalAccess = allowedAgencyIds.includes(0);
            const scopedAgencyOptions = [
                ...(hasGlobalAccess ? [{ value: 0, label: 'Global' }] : []),
                ...(fetchedAgencies?.viewModels || [])
                    .filter(
                        agency =>
                            allowedAgencyIds.includes(agency.id) &&
                            agency.name?.trim().toLowerCase() !== 'global',
                    )
                    .map(agency => ({ value: agency.id, label: agency.name })),
            ];

            const data = mapTemplateForEdit(rawTemplate);
            setCategories(fetchedCategories || []);
            setAgencyOptions(scopedAgencyOptions);
            setTemplateData(data);
            setOriginalTemplateData(data);
        } catch (error) {
            console.error('Error retrieving notification template:', error);
            Toast.error(getText('msg_5c8b9d6e14a8'));
        } finally {
            setIsLoading(false);
        }
    }, [isCreating, notificationId, user?.jwt?.agencies]);

    useEffect(() => {
        loadTemplate();
    }, [loadTemplate]);

    const updateField = (field, value) => {
        setTemplateData(prev => ({ ...prev, [field]: value }));
    };

    const handleTierToggle = (tierId, checked) => {
        setTemplateData(prev => {
            const currentTiers = prev.availableTierIds || [];
            const availableTierIds = checked
                ? [...currentTiers, tierId]
                : currentTiers.filter(id => id !== tierId);
            return { ...prev, availableTierIds };
        });
    };

    const handleDestinationChange = (
        pushDestinationTypeId,
        pushDestinationValue,
    ) => {
        setTemplateData(prev => ({
            ...prev,
            pushDestinationTypeId,
            pushDestinationValue,
        }));
    };

    const validateAll = () => {
        if (!templateData.name?.trim()) {
            return buildValidationResult(false, getText('msg_902f3b0c58ec'));
        }
        if (!templateData.notificationCategoryId) {
            return buildValidationResult(false, getText('msg_ba3dceb428c5'));
        }
        if (templateData.agencyId == null) {
            return buildValidationResult(false, getText('msg_3c4d5e6f7a8b'));
        }
        if (!templateData.subject?.trim()) {
            return buildValidationResult(false, getText('msg_bd3159ff2120'));
        }
        if (!hasMeaningfulHtml(templateData.htmlBody)) {
            return buildValidationResult(false, getText('msg_6f7a8b9c0d1e'));
        }
        if (!hasMeaningfulHtml(templateData.plainTextBody)) {
            return buildValidationResult(false, getText('msg_7a8b9c0d1e2f'));
        }
        if (!hasMeaningfulHtml(templateData.pushBody)) {
            return buildValidationResult(false, getText('msg_8b9c0d1e2f3a'));
        }
        if (!templateData.pushDestinationTypeId) {
            return buildValidationResult(false, getText('msg_4d5e6f7a8b9c'));
        }
        if (
            templateData.pushDestinationTypeId !==
                PushDestinationType.FullMessage &&
            !templateData.pushDestinationValue
        ) {
            return buildValidationResult(false, getText('msg_5e6f7a8b9c0d'));
        }

        return buildValidationResult(true);
    };

    const buildPayload = () => {
        const {
            id,
            name,
            description,
            notificationCategoryId,
            agencyId,
            availableTierIds,
            notificationStatusId,
            subject,
            htmlBody,
            plainTextBody,
            pushBody,
            pushDestinationTypeId,
            pushDestinationValue,
        } = templateData;

        return {
            ...(isCreating ? {} : { id }),
            name,
            description: description || null,
            notificationCategoryId,
            agencyId,
            availableTierIds: availableTierIds || [],
            notificationStatusId: notificationStatusId || 1,
            notificationFrequencyId: NOTIFICATION_FREQUENCY_AD_HOC,
            subject,
            htmlBody,
            plainTextBody,
            pushBody,
            pushDestinationTypeId,
            pushDestinationValue:
                pushDestinationTypeId === PushDestinationType.FullMessage
                    ? null
                    : pushDestinationValue,
        };
    };

    const navigateToTemplateList = useCallback(() => {
        navigate(
            isClientView
                ? ROUTES.MESSAGES_TEMPLATES
                : ROUTES.NOTIFICATION_TEMPLATES,
        );
    }, [navigate, isClientView]);

    const handleCreateFromTemplate = useCallback(() => {
        navigate(ROUTES.MESSAGES_ADD, {
            state: {
                notificationTemplate: {
                    name: templateData.name || '',
                    notificationCategoryId:
                        templateData.notificationCategoryId || null,
                    subject: templateData.subject || '',
                    htmlBody: templateData.htmlBody || '',
                    plainTextBody: templateData.plainTextBody || '',
                    pushBody: templateData.pushBody || '',
                    pushDestinationTypeId:
                        templateData.pushDestinationTypeId ||
                        PushDestinationType.FullMessage,
                    pushDestinationValue:
                        templateData.pushDestinationTypeId ===
                        PushDestinationType.FullMessage
                            ? null
                            : (templateData.pushDestinationValue ?? null),
                    notificationStatusId: 1,
                },
                scheduleAfterSave: false,
            },
        });
    }, [navigate, templateData]);

    const handleSave = async () => {
        const validation = validateAll();
        if (!validation.isValid) {
            Toast.error(validation.message);
            return false;
        }

        const payload = buildPayload();

        setIsSaving(true);
        try {
            if (isCreating) {
                await createNotificationTemplate(payload);
            } else {
                await updateNotificationTemplate(payload);
            }
            Toast.success(getText('msg_7e0d1f8a36ca'));
            navigate(
                isClientView
                    ? ROUTES.MESSAGES_TEMPLATES
                    : ROUTES.NOTIFICATION_TEMPLATES,
            );
            return true;
        } catch (error) {
            console.error('Error saving notification template:', error);
            Toast.error(getText('msg_8f1e2a9b47db'));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const hasUnsavedChanges = () => {
        if (!originalTemplateData) return false;
        return (
            JSON.stringify(templateData) !==
            JSON.stringify(originalTemplateData)
        );
    };

    const handleClose = () => {
        if (!isReadOnly && hasUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_03a2e016b500'),
                onYes: () => handleSave(),
                onNo: () => navigateToTemplateList(),
                noButtonText: getText('msg_ed716b362356'),
                yesButtonText: getText('msg_7af7a6cd22d8'),
            });
            return;
        }
        navigateToTemplateList();
    };

    if (isLoading) {
        return <Spinner width={50} className="pageListloadingSpinner" />;
    }

    return (
        <NotificationPageFrame
            title={isCreating ? getText('msg_d4f1b8c3e620') : templateData.name}
            titleClassName="page-title float-start ms-4 m-0 col-6"
            headerClassName={`col-4 ${styles.headerContent}`}
            headerContent={
                isClientView ? (
                    <span
                        type="button"
                        onClick={handleClose}
                        className="ing-text-buttons"
                    >
                        <i className="bi bi-x me-2" />
                        {getText('msg_bbfa773e5a63')}
                    </span>
                ) : (
                    <>
                        {!isReadOnly && (
                            <span
                                type="button"
                                onClick={() => !isSaving && handleSave()}
                                className="ing-text-buttons"
                                style={{
                                    opacity: isSaving ? 0.6 : 1,
                                    pointerEvents: isSaving ? 'none' : 'auto',
                                }}
                            >
                                <i className="bi bi-check2 me-2" />
                                {getText('msg_efc007a393f6')}
                            </span>
                        )}
                        <span
                            type="button"
                            onClick={handleClose}
                            className="ing-text-buttons"
                        >
                            <i className="bi bi-x me-2" />
                            {getText('msg_bbfa773e5a63')}
                        </span>
                    </>
                )
            }
            bodyClassName={styles['notification-template-page-body']}
            footerContent={
                isClientView ? (
                    <div className={styles.notificationTemplateFooter}>
                        <button
                            type="button"
                            className="button-base ing-button-primary-left-outline"
                            style={
                                isReadOnly
                                    ? {
                                          backgroundColor: '#e5e7eb',
                                          color: '#111827',
                                          borderColor: '#d1d5db',
                                      }
                                    : undefined
                            }
                            onClick={navigateToTemplateList}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </button>

                        <button
                            type="button"
                            className="button-base ing-button-primary-left"
                            onClick={handleCreateFromTemplate}
                        >
                            Create
                        </button>
                    </div>
                ) : null
            }
        >
            <NotificationTemplateDetailsPanel
                templateData={templateData}
                categories={categories}
                agencyOptions={agencyOptions}
                isReadOnly={isReadOnly}
                showTierSection={!isClientView}
                onNameChange={value => updateField('name', value)}
                onDescriptionChange={value => updateField('description', value)}
                onCategoryChange={value =>
                    updateField('notificationCategoryId', value)
                }
                onAgencyChange={value => updateField('agencyId', value)}
                onTierToggle={handleTierToggle}
            />

            <NotificationTemplateContentPreview
                templateData={templateData}
                isCreating={isCreating}
                isReadOnly={isReadOnly}
                showHistory={!isClientView}
                onSubjectChange={value => updateField('subject', value)}
                onHtmlBodyChange={value => updateField('htmlBody', value)}
                onPlainTextBodyChange={value =>
                    updateField('plainTextBody', value)
                }
                onPushBodyChange={value => updateField('pushBody', value)}
                onDestinationChange={handleDestinationChange}
            />
        </NotificationPageFrame>
    );
};

export default NotificationTemplateTabsPage;
